import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { organisationApi, departmentApi } from '../services/organisationDepartment';
import { userOrganisationsApi } from '../services/userOrganisations';
import { logout } from './authSlice';

export const fetchOrganisationsWithDepartments = createAsyncThunk(
    'organisationDepartment/fetchOrganisationsWithDepartments',
    async ({ skip = 0, limit = 20 }, { rejectWithValue, dispatch }) => {
        try {

            const organisations = await organisationApi.getList(skip, limit);
            const items = organisations.data || organisations;

            const organisationsWithDepartments = await Promise.all(
                items.map(async org => {
                    try {
                        const departments = await organisationApi.getDepartmentsByOrganisationId(org.id);
                        return {
                            ...org,
                            departments: departments.data || departments
                        };
                    } catch (error) {
                        console.error(`Failed to fetch departments for organisation ${org.id}:`, error);
                        return {
                            ...org,
                            departments: []
                        };
                    }
                })
            );

            return {
                items: organisationsWithDepartments,
                total: organisations.total || items.length
            };
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchDepartmentsByOrganisation = createAsyncThunk(
    'organisationDepartment/fetchDepartmentsByOrganisation',
    async ({ organisationId, skip = 0, limit = 20 }, { rejectWithValue }) => {
        try {
            return {
                organisationId,
                data: await organisationApi.getDepartmentsByOrganisationId(organisationId, skip, limit)
            };
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchAllDepartments = createAsyncThunk(
    'organisationDepartment/fetchAllDepartments',
    async ({ skip = 0, limit = 20 }, { rejectWithValue }) => {
        try {
            return await departmentApi.getList(skip, limit);
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchUserOrganisations = createAsyncThunk(
    'organisationDepartment/fetchUserOrganisations',
    async ({ userId, skip = 0, limit = 200 }, { rejectWithValue }) => {
        try {
            const organisations = await userOrganisationsApi.getUserOrganisations(userId);
            const items = organisations || [];

            const organisationsWithDepartments = items.map((uo) => {
                const id = uo.organisation_id ?? uo.id;
                const name = uo.organisation_name ?? uo.name ?? 'Неизвестная организация';
                const departments = uo.departments || [];

                return {
                    id,
                    name,
                    ...uo,
                    departments
                };
            });

            return {
                items: organisationsWithDepartments,
                total: organisationsWithDepartments.length
            };
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

const initialState = {
    organisations: [],
    allDepartments: [],
    isLoading: false,
    error: null,
    pagination: {
        skip: 0,
        limit: 20,
        total: 0
    }
};

const organisationDepartmentSlice = createSlice({
    name: 'organisationDepartment',
    initialState,
    reducers: {
        clearData: (state) => {
            state.organisations = [];
            state.allDepartments = [];
            state.error = null;
        },
        setPagination: (state, action) => {
            state.pagination = {
                ...state.pagination,
                ...action.payload
            };
        },
        // Новый reducer для синхронизации данных из authSlice без запроса
        setOrganisationsFromAuth: (state, action) => {
            const items = action.payload || [];
            const organisationsWithDepartments = items.map((uo) => {
                const id = uo.organisation_id ?? uo.id;
                const name = uo.organisation_name ?? uo.name ?? 'Неизвестная организация';
                const departments = uo.departments || [];

                return {
                    id,
                    name,
                    ...uo,
                    departments
                };
            });

            state.organisations = organisationsWithDepartments;
            state.pagination.total = organisationsWithDepartments.length;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganisationsWithDepartments.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchOrganisationsWithDepartments.fulfilled, (state, action) => {
                state.isLoading = false;
                state.organisations = action.payload.items;
                if (action.payload.total) {
                    state.pagination.total = action.payload.total;
                }
            })
            .addCase(fetchOrganisationsWithDepartments.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchUserOrganisations.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserOrganisations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.organisations = action.payload.items;
                state.pagination.total = action.payload.total || action.payload.items?.length || 0;
            })
            .addCase(fetchUserOrganisations.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchDepartmentsByOrganisation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDepartmentsByOrganisation.fulfilled, (state, action) => {
                state.isLoading = false;
                const { organisationId, data } = action.payload;
                const departments = data.items || data;

                const orgIndex = state.organisations.findIndex(o => o.id === organisationId);
                if (orgIndex !== -1) {
                    state.organisations[orgIndex].departments = departments;
                }
            })
            .addCase(fetchDepartmentsByOrganisation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(fetchAllDepartments.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAllDepartments.fulfilled, (state, action) => {
                state.isLoading = false;
                state.allDepartments = action.payload.items || action.payload;
                if (action.payload.total) {
                    state.pagination.total = action.payload.total;
                }
            })
            .addCase(fetchAllDepartments.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.organisations = [];
                state.allDepartments = [];
                state.isLoading = false;
                state.error = null;
                state.pagination = {
                    skip: 0,
                    limit: 20,
                    total: 0
                };
            });
    }
});

export const { clearData, setPagination, setOrganisationsFromAuth } = organisationDepartmentSlice.actions;
export const organisationDepartmentReducer = organisationDepartmentSlice.reducer;